import { useState, useEffect, useCallback, useMemo } from 'react'
import '../styles/dashboard.scss'
import Cookies from 'universal-cookie'
import { useNavigate } from 'react-router-dom'
import { DashboardContents } from './DashboardContents'
import _ from 'lodash'
import {
  habitTypeMap,
  loginCookieId,
  regularTargetValue,
} from '../utils/constants'
import moment, { Moment } from 'moment'
import { AwsCredentials, uploadInspireVideo } from '../utils/s3'
import { Alert, Snackbar } from '@mui/material'
import {
  getAppData,
  giveHighFive,
  logout,
  MiwUpsert,
  NewHighFive,
  ScoreUpsert,
  setMiwProgress,
  setNote,
  setTargets as setTargetsApi,
  toggleScore,
  DashboardData,
  changePassword,
  getHeadlines,
} from '../api'
import { Habit, MiwHabit } from '../components/TargetScores'
import {
  filterDashboardDataToDay,
  makeHabitsFromTargetsAndScores,
  Target,
} from '../utils/dataFormatting'
import { AppLoadingScreen } from './AppLoadingScreen'
import { calcStreaks } from '../utils/calcStreak'
import { HighFiveContent } from '../components/ChatPage'
import { Headline } from '../utils/headlines'
import { habitTypes } from '../components/HabitsPanel'
import { Player } from '../types'
import { InactivePlayerScreen } from './InactivePlayerScreen'

const cookies = new Cookies()

const Dashboard = () => {
  const [dayOffset, setDayOffset] = useState(0)
  const [user] = useState(_.get(cookies.get(loginCookieId), 'user'))
  const [roundStartDay, setRoundStartDay] = useState<Moment | undefined>()
  const [dashboardData, setDashboardData] =
    useState<DashboardData | undefined>()
  const [headlines, setHeadlines] = useState<Headline[] | undefined>()

  const loginCookies = cookies.get(loginCookieId)
  const [awsCredentials] = useState<AwsCredentials>(
    _.get(loginCookies, 'awsCredentials')
  )
  const [sessionKey] = useState(_.get(loginCookies, 'sessionKey'))
  const [playerIsInactive] = useState(_.get(loginCookies, 'playerIsInactive'))

  const selectedDay = useMemo(
    () => moment().subtract(dayOffset, 'days'),
    [dayOffset]
  )

  const habitsOfSelectedDay: Habit[] = useMemo(() => {
    if (dashboardData) {
      return makeHabitsFromTargetsAndScores(
        dashboardData.targets,
        dashboardData.scores.filter((s) =>
          moment(s.day).isSame(selectedDay, 'day')
        )
      )
    } else {
      return []
    }
  }, [selectedDay, dashboardData])

  const [players, setPlayers] = useState<Player[]>([])

  const [isLoading, setLoading] = useState(false)
  const [isUploadingVideo, setIsUploadingVideo] = useState(false)
  const [isCelebrating, setCelebrating] = useState(false)
  const [isShowingNotification, setIsShowingNotification] = useState(false)
  const [notificationText, setNotificationText] = useState<string | undefined>()

  const navigate = useNavigate()

  const handleLogout = useCallback(async () => {
    cookies.remove(loginCookieId)
    navigate('/login')
    await logout(sessionKey)
  }, [navigate, sessionKey])

  // @ts-ignore:
  useEffect(() => {
    if (_.isNil(cookies.get(loginCookieId))) {
      handleLogout()
      return false
    }

    ;(async function () {
      if (!playerIsInactive) {
        try {
          await refreshSpreadsheet()
        } catch (e) {
          console.error(e)
          handleLogout()
        }
      }
    })()
  }, [])

  const refreshSpreadsheet = useCallback(async () => {
    await withLoadingIndicator(async () => {
      const { dashboardData, roundStartDay, players } = await getAppData(
        sessionKey
      )

      setHeadlines(await getHeadlines(sessionKey))

      setDashboardData(dashboardData)
      setRoundStartDay(roundStartDay)
      setPlayers(players)
    })
  }, [sessionKey])

  useEffect(() => {
    if (
      habitsOfSelectedDay.length === habitTypes.length &&
      _.every(habitsOfSelectedDay, (habit) => habit.score >= regularTargetValue)
    ) {
      celebrate()
    }
  }, [habitsOfSelectedDay])

  const celebrate = () => {
    setCelebrating(true)
    setTimeout(() => setCelebrating(false), 10000)
  }

  const handleChangeTargets = async (targets: Target[]) => {
    await withLoadingIndicator(
      async () => await setTargetsApi(sessionKey, targets)
    )
    await refreshSpreadsheet()
  }

  const withLoadingIndicator = async (func: any) => {
    setLoading(true)
    await func()
    setLoading(false)
  }

  const handleSaveNotes = async (notes: string) => {
    await withLoadingIndicator(async () => {
      await setNote(sessionKey, notes, selectedDay.format())
    })
    await refreshSpreadsheet()
  }

  const handleGiveHighFive = async (highFiveContent: HighFiveContent) => {
    if (!dashboardData) return

    const { player, teammate } = dashboardData

    let fileName: string

    await withLoadingIndicator(async () => {
      try {
        if (highFiveContent.videoBlob) {
          setIsUploadingVideo(true)
          fileName = await uploadInspireVideo(
            player.firstName,
            highFiveContent.videoBlob,
            awsCredentials
          )
        }

        const highFive: NewHighFive = {
          recipientId: teammate.id,
          message: highFiveContent.message,
          day: selectedDay.format('L'),
          videoFileName: fileName,
        }
        await giveHighFive(sessionKey, highFive)
      } catch (error) {
        alert(`Oops, your high five didn't go through! Error: ${error}`)
      } finally {
        setIsUploadingVideo(false)
      }
    })
    await refreshSpreadsheet()
  }

  const toggleHabit = async (habit: Habit) => {
    await withLoadingIndicator(async () => {
      const score: ScoreUpsert = {
        completed: habit.score !== 0,
        day: selectedDay.format('L'),
        habitType: habitTypeMap[habit.name],
        id: habit.scoreId,
      }
      await toggleScore(sessionKey, score)
    })
    await refreshSpreadsheet()
  }

  const saveMiwProgress = async (habit: MiwHabit) => {
    await withLoadingIndicator(async () => {
      const score: MiwUpsert = {
        id: habit.scoreId,
        day: selectedDay.format('L'),
        progress: habit.miwProgress,
      }
      await setMiwProgress(sessionKey, score)
    })
    await refreshSpreadsheet()
  }

  const handleGoBackADay = () => {
    setDayOffset(dayOffset + 1)
  }

  const handleGoForwardADay = () => {
    setDayOffset(dayOffset - 1)
  }

  const handleChangePassword = async (password: string) => {
    if (dashboardData) {
      await changePassword(sessionKey, dashboardData?.player.id, password)
      setIsShowingNotification(true)
      setNotificationText('Password changed!')
    }
  }

  if (playerIsInactive) {
    return <InactivePlayerScreen onClick={handleLogout} />
  }

  if (!dashboardData?.player || !roundStartDay) {
    return <AppLoadingScreen />
  }

  return (
    <div style={{ height: '100%' }}>
      <DashboardContents
        dashboardData={filterDashboardDataToDay({
          dashboardData,
          day: selectedDay,
        })}
        headlines={headlines}
        streaks={calcStreaks(dashboardData.scores)}
        habits={habitsOfSelectedDay}
        selectedDay={selectedDay}
        dayOffset={dayOffset}
        onGoBackADay={handleGoBackADay}
        onGoForwardADay={handleGoForwardADay}
        isLoading={isLoading}
        isUploadingVideo={isUploadingVideo}
        isCelebrating={isCelebrating}
        onChangeTargets={handleChangeTargets}
        onChangePassword={handleChangePassword}
        onClickHabit={toggleHabit}
        onSetMiwProgress={saveMiwProgress}
        onSaveNotes={handleSaveNotes}
        user={user}
        players={players}
        onSendHighFive={handleGiveHighFive}
        roundStartDay={roundStartDay}
        onLogout={handleLogout}
      />
      <Snackbar
        open={isShowingNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={4000}
        onClose={() => setIsShowingNotification(false)}
      >
        <Alert
          onClose={() => setIsShowingNotification(false)}
          severity="success"
        >
          {notificationText}
        </Alert>
      </Snackbar>
    </div>
  )
}

export default Dashboard
